// src/components/chatbot/VoiceTips.tsx
import React from 'react';
import { MessageCircle, Mic, Volume2, Lightbulb, X, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface VoiceTipsProps {
  isVisible: boolean;
  onClose: () => void;
  onStartVoice: (mode: 'voice-to-voice' | 'voice-to-text') => void;
}

const VoiceTips: React.FC<VoiceTipsProps> = ({
  isVisible,
  onClose,
  onStartVoice,
}) => {


  const tips = [
    {
      id: 'quick-start',
      icon: <Mic className="h-5 w-5 text-blue-600" />,
      title: 'Quick Voice Commands',
      description: 'Try these common voice commands',
      examples: [
        '"Show me my tasks"',
        '"What\'s my schedule today?"',
        '"Create a new project"',
        '"Check team status"'
      ],
      badge: 'Most Popular'
    },
    {
      id: 'voice-to-voice',
      icon: <Volume2 className="h-5 w-5 text-green-600" />,
      title: 'Voice Conversation',
      description: 'Speak and hear responses instantly',
      examples: [
        'Natural conversation flow',
        'Hands-free operation',
        'Perfect for mobile use',
        'Great while multitasking'
      ],
      badge: 'Hands-free'
    },
    {
      id: 'voice-to-text',
      icon: <MessageCircle className="h-5 w-5 text-purple-600" />,
      title: 'Voice Input',
      description: 'Speak your questions, read the answers',
      examples: [
        'Voice input, text response',
        'Better for detailed info',
        'Easy to copy/share responses',
        'Works in quiet environments'
      ],
      badge: 'Versatile'
    }
  ];

  const quickCommands = [
    { category: 'Project Management', commands: ['Show projects', 'Create new project', 'Project status'] },
    { category: 'Team Management', commands: ['Team overview', 'Assign task', 'Check availability'] },
    { category: 'Reports', commands: ['Weekly report', 'Performance metrics', 'Export data'] },
    { category: 'Help', commands: ['How to use', 'Show features', 'Get help'] }
  ];

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full">
                <Lightbulb className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Voice Assistant Guide</CardTitle>
                <CardDescription>
                  Learn how to use voice commands effectively with Staffluent AI
                </CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-6">
          <div className="space-y-8">
            {/* Voice Modes */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Mic className="h-5 w-5" />
                Choose Your Voice Mode
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {tips.map((tip) => (
                  <Card key={tip.id} className="relative overflow-hidden hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {tip.icon}
                          <CardTitle className="text-sm">{tip.title}</CardTitle>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {tip.badge}
                        </Badge>
                      </div>
                      <CardDescription className="text-xs">
                        {tip.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ul className="space-y-1 mb-3">
                        {tip.examples.map((example, index) => (
                          <li key={index} className="text-xs text-muted-foreground flex items-center gap-1">
                            <div className="w-1 h-1 bg-current rounded-full" />
                            {example}
                          </li>
                        ))}
                      </ul>
                      {tip.id !== 'quick-start' && (
                        <Button 
                          size="sm" 
                          className="w-full text-xs"
                          onClick={() => onStartVoice(tip.id as 'voice-to-voice' | 'voice-to-text')}
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Try {tip.title}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Quick Commands Reference */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Quick Command Reference
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {quickCommands.map((category) => (
                  <Card key={category.category}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">{category.category}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-1">
                        {category.commands.map((command, index) => (
                          <div key={index} className="flex items-center gap-2 text-xs">
                            <Mic className="h-3 w-3 text-muted-foreground" />
                            <span className="font-mono bg-muted px-2 py-1 rounded">
                              "{command}"
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Pro Tips */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Pro Tips</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 text-xs font-bold">1</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Speak Clearly</p>
                      <p className="text-xs text-muted-foreground">Use clear, natural speech for best recognition</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 text-xs font-bold">2</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Be Specific</p>
                      <p className="text-xs text-muted-foreground">Include relevant details like dates, names, or project names</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 text-xs font-bold">3</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Use Context</p>
                      <p className="text-xs text-muted-foreground">Reference previous conversation for follow-up questions</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                    <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-orange-600 text-xs font-bold">4</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Switch Modes</p>
                      <p className="text-xs text-muted-foreground">Change between voice-to-voice and voice-to-text as needed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VoiceTips;